import React, { useEffect, useState } from 'react';
import proptypes from 'prop-types';
import { Skeleton, SkeletonLine } from '@one-thd/sui-atomic-components';
import { dataModel } from './dataModel';
import { useApplianceFuelType } from './hooks/useApplianceFuelType';
import { FuelTypeAlert, FuelTypeDrawer, FuelTypeProvider } from './components';
import { ENTRY_TYPES, IMAGES } from './constants';

const { string, bool, shape, arrayOf, oneOf } = proptypes;

export const ApplianceFuelType = ({
  itemId,
  entryType,
  position,
  product,
  metadata,
  search,
  imageCacheEnabled,
  returnEmptyForFusion
}) => {
  useEffect(() => { LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('appliance-fuel-type.ready'); }, []);

  const [isCaching, setIsCaching] = useState(imageCacheEnabled && typeof window !== 'undefined');

  /* istanbul ignore next */
  useEffect(() => {
    const cacheImages = async () => {
      try {
        if (!ENTRY_TYPES.includes(entryType)) throw new Error('No entryType provided to ApplianceFuelType');
        await Promise.all(Object.values(IMAGES).map(async (src) => {
          return new Promise((resolve, reject) => {
            const image = new Image();
            image.src = src;
            image.onload = resolve;
            image.onerror = reject;
          });
        }));
        setIsCaching(false);
      } catch (er) {
        console.log(er);
        setIsCaching(false);
      }
    };
    if (imageCacheEnabled) cacheImages();
  }, [imageCacheEnabled, entryType]);

  const {
      types,
      categories,
      isDryer,
      isRange,
      isEither,
      hasDualFuel,
      loading,
      error
    } = useApplianceFuelType({
      itemId,
      product,
      metadata,
      search
    });

  if (isCaching || loading) {
    return (
      <Skeleton
        data-testid="appliance-fuel-type-loader"
        grow
        disablePadding
      >
        <SkeletonLine variant="single" />
      </Skeleton>
    );
  }

  if ((!itemId && !product && !search)
      || !(loading || !error)
      || !categories?.length
      || !ENTRY_TYPES.includes(entryType)) {
        return returnEmptyForFusion ? <div /> : null;
      }

  return (
    <div
      className="sui-pt-3"
      data-testid="appliance-fuel-type"
      data-component="ApplianceFuelType"
    >
      <FuelTypeProvider
        entryType={entryType}
        position={position}
        types={types}
        categories={categories}
        drawerStatus={{
          isDryer,
          isRange,
          isEither,
          hasDualFuel
        }}
      >
        <FuelTypeAlert />
        <FuelTypeDrawer />
      </FuelTypeProvider>
    </div>
  );
};

ApplianceFuelType.propTypes = {
  itemId: string,
  entryType: oneOf(ENTRY_TYPES),
  position: oneOf(['left', 'right']),
  imageCacheEnabled: bool,
  returnEmptyForFusion: bool,
  product: shape({
    itemId: string,
    dataSource: string,
    identifiers: shape({
      isSuperSku: bool,
      parentId: string
    }),
    specificationGroup: arrayOf(shape({
      specTitle: string,
      specifications: arrayOf(shape({
        specName: string,
        specValue: string
      }))
    }))
  }),
  metadata: shape({
    attributes: arrayOf(shape({
      attributeValues: arrayOf(shape({
        value: string
      })),
      attributeName: string
    })),
    superDuperSku: shape({
      attributes: arrayOf(shape({
        attributeName: string,
        attributeValues: arrayOf(shape({
          selected: bool,
          value: string
        }))
      }))
    })
  }),
  search: shape({
    dimensions: arrayOf(shape({
      label: string,
      refinements: arrayOf(shape({
        label: string
      }))
    })),
    primaryFilters: arrayOf(shape({
      label: string,
      refinements: arrayOf(shape({
        label: string
      }))
    }))
  })
};

ApplianceFuelType.defaultProps = {
  itemId: '',
  entryType: '',
  position: 'right',
  imageCacheEnabled: false,
  returnEmptyForFusion: false,
  product: null,
  metadata: null,
  search: null
};

ApplianceFuelType.dataModel = dataModel;

ApplianceFuelType.displayName = 'ApplianceFuelType';
